import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Plus, Pencil, Trash2, Users, Lock, CheckCircle2, KeyRound } from 'lucide-react';

const SystemBadge = () => (
  <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
    <Lock className="w-2.5 h-2.5 inline mr-0.5" /> System
  </span>
);

export default function Roles() {
  const { user } = useAuth();
  const canManageRoles = user?.is_primary_admin || (user?.permissions || []).includes('roles.manage') || user?.role === 'admin';
  const [roles, setRoles] = useState([]);
  const [permModules, setPermModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // role being edited
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get('/rbac/roles'), api.get('/rbac/permissions')]);
      setRoles(r.data);
      setPermModules(p.data.modules);
    } catch (e) { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const deleteRole = async (r) => {
    if (!window.confirm(`Delete the role "${r.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/rbac/roles/${r.id}`);
      toast.success(`"${r.name}" deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Access control</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Roles & Permissions</h1>
          <p className="text-slate-500 mt-1">System roles are seeded with sensible defaults. Build custom roles for unusual access patterns.</p>
        </div>
        {canManageRoles && (
          <Button data-testid="create-role-button" onClick={() => setCreateOpen(true)}
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> New role
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map(r => (
            <div key={r.id} data-testid={`role-card-${r.code || r.id}`}
                 className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 text-[#3D4A8E] flex items-center justify-center">
                      <Shield className="w-4.5 h-4.5" strokeWidth={1.8} />
                    </div>
                    {r.system && <SystemBadge />}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 leading-snug mt-1">{r.name}</h3>
                  {r.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.description}</p>}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <KeyRound className="w-3 h-3" /> <b className="text-slate-900">{r.permissions.length}</b> permissions
                </span>
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Users className="w-3 h-3" /> <b className="text-slate-900">{r.assigned_count}</b> users
                </span>
              </div>
              {canManageRoles && !r.system && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`edit-role-${r.id}`}
                          onClick={() => setEditing(r)}
                          className="rounded-full flex-1 h-8 text-xs">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`delete-role-${r.id}`}
                          onClick={() => deleteRole(r)}
                          className="rounded-full flex-1 h-8 text-xs text-rose-600 hover:bg-rose-50 border-rose-200">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              )}
              {canManageRoles && r.system && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                  <Button size="sm" variant="outline" disabled
                          className="rounded-full flex-1 h-8 text-xs opacity-50 cursor-not-allowed">
                    System role — read-only
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <RoleDialog key={createOpen ? 'create' : 'none'}
                  open={createOpen} onOpenChange={setCreateOpen}
                  permModules={permModules} role={null}
                  busy={busy} setBusy={setBusy}
                  onSaved={() => { setCreateOpen(false); load(); }} />
      <RoleDialog key={`edit-${editing?.id || 'none'}`}
                  open={!!editing} onOpenChange={(o) => !o && setEditing(null)}
                  permModules={permModules} role={editing}
                  busy={busy} setBusy={setBusy}
                  onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function RoleDialog({ open, onOpenChange, permModules, role, busy, setBusy, onSaved }) {
  const isEdit = !!role;
  // Initialize from props at mount; parent passes a `key` so we remount when `role` changes.
  const [form, setForm] = useState(() => role
    ? { name: role.name, description: role.description || '' }
    : { name: '', description: '' });
  const [perms, setPerms] = useState(() => new Set(role?.permissions || []));

  const toggle = (code) => {
    const next = new Set(perms);
    next.has(code) ? next.delete(code) : next.add(code);
    setPerms(next);
  };
  const toggleModule = (mod) => {
    const codes = mod.permissions.map(p => p.code);
    const allOn = codes.every(c => perms.has(c));
    const next = new Set(perms);
    codes.forEach(c => allOn ? next.delete(c) : next.add(c));
    setPerms(next);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Role name is required'); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, permissions: [...perms] };
      if (isEdit) {
        await api.patch(`/rbac/roles/${role.id}`, payload);
        toast.success(`"${form.name}" updated`);
      } else {
        await api.post('/rbac/roles', payload);
        toast.success(`"${form.name}" created`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="role-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isEdit ? 'Edit role' : 'New custom role'}</DialogTitle>
          <DialogDescription>Pick the permissions this role should grant. Users with this role can perform every permitted action.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="text-xs font-semibold text-slate-600">Name *</label>
              <Input data-testid="role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-600">Description</label>
              <Textarea data-testid="role-description" rows={1} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" />
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Permissions</div>
              <span className="text-xs text-slate-500"><b className="text-slate-900">{perms.size}</b> selected</span>
            </div>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2" data-testid="perm-grid">
              {permModules.map(mod => {
                const allOn = mod.permissions.every(p => perms.has(p.code));
                const someOn = !allOn && mod.permissions.some(p => perms.has(p.code));
                return (
                  <div key={mod.name} className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{mod.name}</div>
                      <button type="button" onClick={() => toggleModule(mod)}
                              data-testid={`module-toggle-${mod.name}`}
                              className={`text-[10px] font-bold px-2 py-1 rounded-full transition ${
                                allOn ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                someOn ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                         'bg-slate-50 text-slate-500 border border-slate-200 hover:border-[#3D4A8E]/40'}`}>
                        {allOn ? 'ALL ON' : someOn ? 'PARTIAL' : 'NONE'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {mod.permissions.map(p => (
                        <label key={p.code} data-testid={`perm-${p.code}`}
                               className={`flex items-start gap-2 text-xs px-2 py-1.5 rounded-lg cursor-pointer transition ${
                                 perms.has(p.code) ? 'bg-indigo-50 text-slate-900' : 'hover:bg-slate-50 text-slate-700'}`}>
                          <input type="checkbox" checked={perms.has(p.code)}
                                 onChange={() => toggle(p.code)}
                                 className="mt-0.5 accent-[#3D4A8E]" />
                          <span className="min-w-0">
                            <span className="font-medium">{p.label}</span>
                            <code className="block text-[10px] text-slate-400">{p.code}</code>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-role"
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]">
            <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create role')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
